import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { InvitationDialogContent } from './InvitationDialogContent';
import { useAcceptInvitationById, useRejectInvitation } from '../hooks/useInvitationMutations';
import type { MyInvitation, InvitationView } from '../types';

interface InvitationDialogProps {
  invitation: MyInvitation | null;
  open: boolean;
  onClose: () => void;
}

function toInvitationView(invitation: MyInvitation): InvitationView {
  return {
    tenantName: invitation.tenantName,
    tenantAvatarUrl: invitation.tenantAvatarUrl,
    inviterName: invitation.inviterName,
    role: invitation.role,
    membersCount: invitation.membersCount,
  };
}

export function InvitationDialog({ invitation, open, onClose }: InvitationDialogProps) {
  const { t } = useTranslation('invitations');
  const { mutateAsync: acceptInvitation, isPending: isAccepting } = useAcceptInvitationById();
  const { mutateAsync: rejectInvitation, isPending: isRejecting } = useRejectInvitation();
  const { confirm, isOpen: isConfirmOpen, options, handleConfirm, handleCancel } = useConfirm();

  async function handleAccept() {
    if (!invitation) return;
    try {
      await acceptInvitation(invitation.id);
      onClose();
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  async function handleReject() {
    if (!invitation) return;
    const { confirmed } = await confirm({
      title: t('dialog.confirmReject.title'),
      description: t('dialog.confirmReject.description'),
      confirmLabel: t('dialog.confirmReject.confirm'),
      cancelLabel: t('dialog.confirmReject.cancel'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await rejectInvitation(invitation.id);
      onClose();
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) onClose();
        }}
      >
        <DialogContent data-testid="invitations-dialog" className="w-full max-w-md">
          <DialogHeader>
            <DialogTitle>{t('dialog.title')}</DialogTitle>
          </DialogHeader>
          {invitation && (
            <InvitationDialogContent
              invitation={toInvitationView(invitation)}
              onAccept={handleAccept}
              onReject={handleReject}
              isPending={isAccepting || isRejecting}
            />
          )}
        </DialogContent>
      </Dialog>

      {options && (
        <ConfirmDialog
          open={isConfirmOpen}
          onOpenChange={(v) => {
            if (!v) handleCancel();
          }}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
}
